import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Home, RotateCcw, Trophy } from 'lucide-react';
import confetti from 'canvas-confetti';
import axios from 'axios';

const STAR_URL =
  'https://static.prod-images.emergentagent.com/jobs/0e7ec82b-be21-4167-ae09-299c58207254/images/51c2f4d391b08733a1282c5c803c88045bd7e3e5993310b66fbe2ae387825c7c.png';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GKResultsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const savedRef = useRef(false);

  const score = parseInt(searchParams.get('score') || '0', 10);
  const total = parseInt(searchParams.get('total') || '20', 10);
  const percentage = total > 0 ? Math.round((score / total) * 100) : 0;
  const stars = percentage >= 90 ? 3 : percentage >= 70 ? 2 : percentage >= 50 ? 1 : 0;

  useEffect(() => {
    if (!savedRef.current) {
      savedRef.current = true;
      axios
        .post(`${API}/gk/results`, { player_name: localStorage.getItem('gaurik_player_name') || 'Player', score, total_questions: total })
        .catch(console.error);
    }

    if (percentage >= 50) {
      const end = Date.now() + 2500;
      const frame = () => {
        confetti({ particleCount: 4, angle: 60, spread: 55, origin: { x: 0 }, colors: ['#FF9933', '#138808', '#000080'] });
        confetti({ particleCount: 4, angle: 120, spread: 55, origin: { x: 1 }, colors: ['#FF9933', '#138808', '#000080'] });
        if (Date.now() < end) requestAnimationFrame(frame);
      };
      frame();
    }
  }, [percentage, score, total]);

  const getMessage = () => {
    if (percentage >= 90) return 'BRILLIANT!';
    if (percentage >= 70) return 'GREAT JOB!';
    if (percentage >= 50) return 'WELL DONE!';
    return 'KEEP LEARNING!';
  };

  const getSubMsg = () => {
    if (percentage >= 90) return 'You really know your India!';
    if (percentage >= 70) return "You're a GK champion!";
    if (percentage >= 50) return 'Good effort, keep exploring!';
    return 'Every quiz makes you smarter!';
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center p-6"
      style={{ background: 'linear-gradient(135deg, #FFF5E6 0%, #FFFFFF 50%, #E8F5E9 100%)' }}
    >
      <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8 max-w-md w-full text-center">
        <Trophy className="w-14 h-14 mx-auto mb-3" style={{ color: '#FF9933' }} />

        <h1
          data-testid="gk-results-message"
          className="font-heading text-3xl sm:text-4xl font-black text-gray-900 mb-1"
        >
          {getMessage()}
        </h1>
        <p className="font-body text-sm text-gray-500 mb-5">{getSubMsg()}</p>

        {/* Stars */}
        <div className="flex justify-center gap-3 mb-5" data-testid="gk-results-stars">
          {[1, 2, 3].map((i) => (
            <img
              key={i}
              src={STAR_URL}
              alt={`star ${i}`}
              className={`w-14 h-14 animate-star-in ${i <= stars ? '' : 'grayscale opacity-30'}`}
              style={{ animationDelay: `${i * 250}ms` }}
            />
          ))}
        </div>

        {/* Score */}
        <p data-testid="gk-results-score" className="font-heading text-5xl font-black text-gray-900 mb-1">
          {score}/{total}
        </p>
        <p className="font-body text-base font-bold text-gray-500 mb-6">{percentage}% correct</p>

        {/* Tricolour divider */}
        <div className="flex h-1.5 rounded-full overflow-hidden mb-6">
          <div className="flex-1" style={{ background: '#FF9933' }} />
          <div className="flex-1 bg-white" />
          <div className="flex-1" style={{ background: '#138808' }} />
        </div>

        {/* Buttons */}
        <div className="flex flex-col gap-3">
          <button
            data-testid="gk-play-again"
            onClick={() => navigate('/gk-quiz/play')}
            className="bg-[#FF9933] text-white font-black text-lg py-4 px-8 rounded-full border-b-[6px] border-[#cc7a29] active:border-b-0 active:translate-y-[6px] transition-all hover:bg-[#ff8010] flex items-center justify-center gap-2 shadow-lg"
          >
            <RotateCcw className="w-6 h-6" />
            Play Again
          </button>
          <button
            data-testid="gk-go-home"
            onClick={() => navigate('/')}
            className="bg-[#138808] text-white font-black text-lg py-4 px-8 rounded-full border-b-[6px] border-[#0e6506] active:border-b-0 active:translate-y-[6px] transition-all hover:bg-[#0f6d06] flex items-center justify-center gap-2 shadow-lg"
          >
            <Home className="w-6 h-6" />
            Back to Studio
          </button>
        </div>
      </div>
    </div>
  );
}
